import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, ShoppingBag, Store, X } from "lucide-react";
import Logo from "@/components/Logo";
import { useShop } from "@/context/ShopContext";
import { useContent } from "@/context/ContentContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const FALLBACK_LINKS = [
  { label: "Mugs & Cups", to: "/shop?cat=Mugs" },
  { label: "Planters & Pots", to: "/shop?cat=Planters" },
  { label: "Decor", to: "/shop?cat=Decor Items" },
];

const AnnouncementBar = () => {
  const { c } = useContent();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("riaas_ann_off") === "1");
  if (dismissed || c("announcement_enabled", "false") !== "true") return null;
  const link = c("announcement_link", "");
  return (
    <div className="relative flex h-10 items-center justify-center gap-3 bg-terra px-10 text-cream" data-testid="announcement-bar">
      <p className="truncate font-mono text-[10px] font-medium uppercase tracking-[0.22em] sm:text-[11px]">
        {c("announcement_text", "")}
      </p>
      {link && (
        <Link
          to={link}
          data-testid="announcement-link"
          className="shrink-0 border-b border-cream/60 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] transition-colors hover:border-cream sm:text-[11px]"
        >
          {c("announcement_link_label", "Shop now")} →
        </Link>
      )}
      <button
        onClick={() => {
          sessionStorage.setItem("riaas_ann_off", "1");
          setDismissed(true);
        }}
        aria-label="Dismiss announcement"
        data-testid="announcement-dismiss"
        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-cream/70 transition-colors hover:text-cream"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

const Header = () => {
  const { cartCount, setCartOpen, categories } = useShop();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const navLinks = [
    { label: "Shop All", to: "/shop" },
    ...(categories.length
      ? categories.slice(0, 3).map((cat) => ({ label: cat.label, to: `/shop?cat=${encodeURIComponent(cat.name)}` }))
      : FALLBACK_LINKS),
  ];

  return (
    <header className="fixed inset-x-0 top-0 z-40">
      <AnnouncementBar />
      <div className="border-b border-linen/80 bg-cream/85 backdrop-blur-xl">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/" aria-label="Riaas Collective home">
            <Logo />
          </Link>

          <nav className="hidden items-center gap-8 md:flex">
            {navLinks.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                className="text-sm font-medium text-clay/80 transition-colors duration-200 hover:text-terra"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/admin"
              data-testid="nav-admin-portal-button"
              className="hidden items-center gap-1.5 rounded-full border border-linen bg-white/60 px-3.5 py-2 text-xs font-semibold text-clay transition-colors duration-200 hover:border-terra hover:text-terra sm:flex"
            >
              <Store className="h-3.5 w-3.5" />
              Admin
            </Link>
            <button
              data-testid="nav-cart-toggle-button"
              onClick={() => setCartOpen(true)}
              className="relative flex items-center gap-2 rounded-full bg-espresso px-4 py-2.5 text-sm font-semibold text-cream transition-colors duration-200 hover:bg-terra active:scale-[0.97]"
              aria-label="Open cart"
            >
              <ShoppingBag className="h-4 w-4" />
              <span className="hidden sm:inline">Cart</span>
              {cartCount > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-terra px-1 font-mono text-[10px] font-semibold text-white"
                >
                  {cartCount}
                </span>
              )}
            </button>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="rounded-full p-2 text-clay md:hidden" aria-label="Open menu" data-testid="mobile-menu-button">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 bg-cream">
                <div className="mt-8 flex flex-col gap-6">
                  {navLinks.map((l) => (
                    <Link
                      key={l.label}
                      to={l.to}
                      onClick={() => setMobileOpen(false)}
                      className="font-serif text-2xl text-espresso transition-colors hover:text-terra"
                    >
                      {l.label}
                    </Link>
                  ))}
                  <button
                    onClick={() => {
                      setMobileOpen(false);
                      navigate("/admin");
                    }}
                    className="text-left font-mono text-xs uppercase tracking-[0.22em] text-terra"
                  >
                    Admin
                  </button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
